import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Minus, Plus, CheckCircle2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";

function badgeClass(t) {
  return {
    discount: "bg-[#F43F5E] text-white",
    hot: "bg-[#FFB020] text-[#111827]",
    new: "bg-emerald-500 text-white",
    deal: "bg-[#4B2AAD] text-white",
    refurbished: "bg-emerald-100 text-emerald-700 border border-emerald-300",
    limited: "bg-red-100 text-red-600 border border-red-200",
    best: "bg-[#FFE9B0] text-[#8A5A00]",
  }[t] || "bg-[#F43F5E] text-white";
}

export default function ProductCard({ product, showAddToCart = true, layout = "vertical" }) {
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const wished = isInWishlist(product.id);

  const savings = (product.mrp || 0) - (product.price || 0);
  const specs = product.features?.slice(0, 3);

  // The card has two visual states:
  //   1. Compact CTA — a full-width "Add to Cart" button (icon + text).
  //   2. Picker     — a [- pendingQty +] spinner + an icon-only cart button.
  //
  // Flow (every click on the Add-to-Cart button commits items to the cart):
  //   - Compact click: adds 1 to cart AND opens the picker (pendingQty = 1).
  //   - +/- inside the picker: adjust local pendingQty only (min 1, capped
  //     at the product's available stock; no cart change).
  //   - Picker cart-icon click: adds `pendingQty` more items and then
  //     collapses back to the compact state so the CTA visually confirms
  //     the successful add.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pendingQty, setPendingQty] = React.useState(1);
  const [flash, setFlash] = React.useState(false);
  const [adding, setAdding] = React.useState(false);

  // Inventory guard — respect `in_stock` / `stock` shipped on every
  // normalized product. `stockCap` is Infinity when the product doesn't
  // expose a numeric stock so we don't accidentally over-constrain.
  const inStock = product.in_stock !== false;
  const stockCap = Number.isFinite(Number(product.stock)) && Number(product.stock) > 0
    ? Number(product.stock)
    : Infinity;

  const handleAdd = async () => {
    if (adding || !inStock) return;
    // Compact click adds exactly 1 unit and *opens* the picker so the
    // shopper can dial up more items on the next tap.
    // Picker click commits whatever `pendingQty` they chose and *closes*
    // the picker so the CTA snaps back to its compact "Add to Cart" form,
    // confirming the successful commit.
    const wasPickerOpen = pickerOpen;
    const qtyToAdd = wasPickerOpen ? Math.min(pendingQty, stockCap) : 1;
    setAdding(true);
    try {
      await addToCart(product.id, qtyToAdd);
      setPickerOpen(!wasPickerOpen);
      setPendingQty(1);
      setFlash(true);
      setTimeout(() => setFlash(false), 2500);
    } finally {
      setAdding(false);
    }
  };

  const handleMinus = () => {
    // +/- only adjust local pendingQty; they never touch the cart. Floor
    // is 1 so the next commit always adds at least one item.
    setPendingQty((n) => Math.max(1, n - 1));
  };

  const handlePlus = () => {
    setPendingQty((n) => Math.min(stockCap, n + 1));
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  return (
    <div data-testid={`product-card-${product.id}`} className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-[0_20px_40px_rgba(75,42,173,0.15)] hover:-translate-y-1 flex flex-col">
      {/* Success flash banner */}
      {flash && (
        <div className="absolute top-0 inset-x-0 z-20 bg-[#F3EFFC] text-[#4B2AAD] text-xs font-semibold px-3 py-2 flex items-center gap-1.5 border-b border-[#4B2AAD]/15">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Added to cart successfully!
        </div>
      )}

      {/* Top row: save badge + wishlist */}
      <div className="absolute top-2.5 inset-x-2.5 z-10 flex items-start justify-between pointer-events-none">
        <div className="flex flex-col gap-1 pointer-events-auto">
          {product.badge && product.badge_type !== "discount" && (
            <span className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badgeClass(product.badge_type)}`}>
              {product.badge}
            </span>
          )}
          {savings > 0 && (
            <span className="inline-flex items-center rounded-full bg-[#F3EFFC] px-2.5 py-1 text-[11px] font-bold text-[#4B2AAD]">
              Save ₹{savings.toLocaleString()}
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid={`wishlist-btn-${product.id}`}
          onClick={handleWishlist}
          className="pointer-events-auto h-8 w-8 rounded-full bg-white/95 backdrop-blur border border-gray-100 flex items-center justify-center hover:bg-white transition-colors"
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          aria-pressed={wished}
        >
          <Heart
            className={`h-4 w-4 transition-colors ${wished ? "text-[#F43F5E] fill-[#F43F5E]" : "text-gray-500 hover:text-[#F43F5E]"}`}
          />
        </button>
      </div>

      <Link to={`/product/${product.id}`} className="block product-img-wrap aspect-square bg-white pt-10">
        <img
          src={product.image || "/product-placeholder.svg"}
          alt={product.name}
          loading="lazy"
          onError={(e) => {
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = "1";
              e.currentTarget.src = "/product-placeholder.svg";
            }
          }}
          onLoad={(e) => {
            // Some dev servers respond 200 with SPA index.html for missing
            // /product-images/*.jpg paths — the browser silently loads that
            // as a zero-size "image" without firing onError. Detect that
            // here and swap in the placeholder so cards never render blank.
            const el = e.currentTarget;
            if (
              !el.dataset.fallback &&
              (el.naturalWidth === 0 || el.naturalHeight === 0)
            ) {
              el.dataset.fallback = "1";
              el.src = "/product-placeholder.svg";
            }
          }}
          className="w-full h-full object-contain p-3"
        />
      </Link>

      <div className="p-3 pt-2 flex flex-col flex-1">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display text-sm font-bold text-gray-900 line-clamp-2 min-h-[38px] hover:text-[#4B2AAD] transition-colors">{product.name}</h3>
        </Link>

        {/* Reserved spec-line slot — kept at a fixed 16px height so cards
            without any specs stay the same vertical size as cards with a
            spec line. Keeps the Add-to-Cart CTAs perfectly aligned across
            every 6-column grid on the site. */}
        <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1 min-h-[16px]">
          {specs && specs.length > 0 ? specs.slice(0, 3).join(" • ") : "\u00A0"}
        </p>

        <div className="mt-2 flex items-baseline gap-2">
          {product.mrp > product.price && (
            <span className="text-xs text-gray-400 line-through">₹{product.mrp.toLocaleString()}</span>
          )}
          <span className="text-lg font-extrabold text-[#4B2AAD]">₹{product.price?.toLocaleString()}</span>
        </div>

        {/* Bottom-anchored CTA block. `mt-auto` pushes it to the bottom of
            the card so every card in a 6-column grid aligns its
            Add-to-Cart button on the same baseline regardless of the
            title / spec height above. */}
        {showAddToCart && (
          <div className="mt-3 mt-auto">
            {pickerOpen ? (
              <div className="flex items-center gap-1.5">
                {/* Compact qty selector so the CTA gets most of the
                    horizontal room even in tight 6-column grids. */}
                <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden bg-white shrink-0">
                  <button
                    type="button"
                    data-testid={`card-qty-minus-${product.id}`}
                    onClick={handleMinus}
                    className="px-1.5 py-1 hover:bg-gray-50"
                    aria-label="Decrease quantity"
                  ><Minus className="h-3 w-3" /></button>
                  <span data-testid={`card-qty-${product.id}`} className="w-5 text-center text-xs font-bold">{pendingQty}</span>
                  <button
                    type="button"
                    data-testid={`card-qty-plus-${product.id}`}
                    onClick={handlePlus}
                    disabled={pendingQty >= stockCap}
                    className="px-1.5 py-1 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Increase quantity"
                  ><Plus className="h-3 w-3" /></button>
                </div>
                <button
                  type="button"
                  data-testid={`add-to-cart-${product.id}`}
                  onClick={handleAdd}
                  disabled={adding || !inStock}
                  aria-label="Add to Cart"
                  title="Add to Cart"
                  className="flex-1 min-w-0 inline-flex items-center justify-center rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2.5 transition-all active:scale-[0.98]"
                >
                  <ShoppingCart className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid={`add-to-cart-${product.id}`}
                onClick={handleAdd}
                disabled={adding || !inStock}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-sm font-bold py-2.5 transition-all active:scale-[0.98] whitespace-nowrap"
              >
                <ShoppingCart className="h-4 w-4" /> {inStock ? "Add to Cart" : "Out of Stock"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
